import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Transaction,
  Category,
  Account,
  Tenant,
  Profile,
  TransactionAttachment,
  UpdateTransactionRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateAccountRequest,
  UpdateAccountRequest,
  CreateTransactionRequest,
  CreateTransactionsMetaDataCommand,
  CreateSharingRequestCommand,
} from '@/types/api';
import { apiService, mapToApiError } from '@/services/api';
import { emitToast } from '@/utils/toast';

/**
 * Common error handler for mutations that provides user-friendly error messages
 */
const handleMutationError = (error: unknown, _operation: string) => {
  const apiError = mapToApiError(error);
  emitToast({
    message: apiError.message,
    severity: 'error',
  });

  // Re-throw as ApiError for components that want to handle it locally
  throw apiError;
};

/**
 * Query keys for consistent caching
 */
export const queryKeys = {
  all: ['budgetSpeech'] as const,
  transactions: () => [...queryKeys.all, 'transactions'] as const,
  transactionsByAccount: (accountId: string) =>
    [...queryKeys.transactions(), 'account', accountId] as const,
  transactionsByHash: (hash: string) => [...queryKeys.transactions(), 'hash', hash] as const,
  categories: () => [...queryKeys.all, 'categories'] as const,
  accounts: () => [...queryKeys.all, 'accounts'] as const,
  accountDetails: (accountId: string) => [...queryKeys.accounts(), 'details', accountId] as const,
  tenant: () => [...queryKeys.all, 'tenant'] as const,
  profile: () => [...queryKeys.all, 'profile'] as const,
  sharing: () => [...queryKeys.all, 'sharing'] as const,
  sharingConfig: () => [...queryKeys.sharing(), 'config'] as const,
} as const;

// ============================================================================
// HEALTH CHECK
// ============================================================================

export function useHealthCheck(enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.all, 'health'],
    queryFn: () => apiService.healthCheck(),
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
    enabled,
  });
}

// ============================================================================
// TRANSACTIONS
// ============================================================================

export function useTransactions(enabled = true) {
  return useQuery({
    queryKey: queryKeys.transactions(),
    queryFn: () => apiService.getTransactions(),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes - transactions change frequently
    gcTime: 5 * 60 * 1000, // 5 minutes - shorter cache time for dynamic data
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
}

export function useTransactionsByAccount(accountId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.transactionsByAccount(accountId),
    queryFn: () => apiService.fetchTransactionsByAccount(accountId),
    enabled: enabled && Boolean(accountId),
    staleTime: 2 * 60 * 1000, // 2 minutes - transactions change frequently
    gcTime: 5 * 60 * 1000, // 5 minutes - shorter cache time for dynamic data
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
}

export function useTransactionsByHash(hash: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.transactionsByHash(hash),
    queryFn: () => apiService.fetchTransactions(hash),
    enabled: enabled && Boolean(hash),
  });
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      id,
      transaction,
    }: {
      accountId: string;
      id: number;
      transaction: UpdateTransactionRequest;
    }) => apiService.updateTransaction(accountId, id, transaction),
    onSuccess: (updatedTransaction, { accountId }) => {
      // Invalidate and refetch transaction lists
      queryClient.invalidateQueries({ queryKey: queryKeys.transactions() });
      queryClient.invalidateQueries({ queryKey: queryKeys.transactionsByAccount(accountId) });

      // Update specific transaction in cache if possible
      queryClient.setQueryData<Transaction[]>(queryKeys.transactions(), old => {
        if (!old) {
          return old;
        }
        return old.map(t => (t.id === updatedTransaction.id ? updatedTransaction : t));
      });
    },
    onError: error => handleMutationError(error, 'update transaction'),
  });
}

export function useAddTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      request,
    }: {
      accountId: string;
      request: CreateTransactionRequest;
    }) => apiService.addTransaction(accountId, request),
    onSuccess: (newTx, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.transactions() });
      queryClient.invalidateQueries({ queryKey: queryKeys.transactionsByAccount(accountId) });

      queryClient.setQueryData<Transaction[]>(queryKeys.transactions(), old => {
        if (!old) {
          return [newTx];
        }
        return [newTx, ...old];
      });
    },
  });
}

export function useAddTransactionMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      transactionId,
      createTransactionsMetaDataCommand,
    }: {
      accountId: string;
      transactionId: number;
      createTransactionsMetaDataCommand: CreateTransactionsMetaDataCommand;
    }) =>
      apiService.addTransactionMetadata(
        accountId,
        transactionId,
        createTransactionsMetaDataCommand
      ),
    onSuccess: (_, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.transactionsByAccount(accountId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.transactions() });
    },
  });
}

export function useReplaceTransactionMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      transactionId,
      metadataList,
    }: {
      accountId: string;
      transactionId: number;
      metadataList: Array<{ amount: number; description: string; category: string }>;
    }) => apiService.replaceTransactionMetadata(accountId, transactionId, metadataList),
    onSuccess: (_, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.transactionsByAccount(accountId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.transactions() });
    },
  });
}

export function useAddTransactionAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      transactionId,
      attachment,
      content,
    }: {
      accountId: string;
      transactionId: number;
      attachment: TransactionAttachment;
      content: File;
    }) => apiService.addTransactionAttachment(accountId, transactionId, attachment, content),
    onSuccess: (_, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.transactionsByAccount(accountId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.transactions() });
    },
  });
}

export function useDeleteTransactionAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      transactionId,
      attachmentId,
    }: {
      accountId: string;
      transactionId: number;
      attachmentId: number;
    }) => apiService.deleteTransactionAttachment(accountId, transactionId, attachmentId),
    onSuccess: (_, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.transactionsByAccount(accountId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.transactions() });
    },
  });
}

export function useGetTransactionAttachment() {
  return useMutation({
    mutationFn: ({
      accountId,
      transactionId,
      attachmentId,
    }: {
      accountId: string;
      transactionId: number;
      attachmentId: number;
    }) => apiService.getTransactionAttachment(accountId, transactionId, attachmentId),
  });
}

export function useFetchTransactionsByAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ accountId, hash }: { accountId: string; hash?: string }) =>
      apiService.fetchTransactionsByAccount(accountId, hash),
    onSuccess: (_, { accountId }) => {
      // Only invalidate the specific account's transactions
      // The main transactions list will be invalidated separately after all accounts are processed
      queryClient.invalidateQueries({ queryKey: queryKeys.transactionsByAccount(accountId) });
    },
  });
}

export function useGetCategorySuggestions() {
  return useMutation({
    mutationFn: ({ accountId, transactionIds }: { accountId: string; transactionIds: number[] }) =>
      apiService.getCategorySuggestions(accountId, transactionIds),
    onError: error => handleMutationError(error, 'get category suggestions'),
  });
}

// ============================================================================
// CATEGORIES
// ============================================================================

export function useCategories(enabled = true) {
  return useQuery({
    queryKey: queryKeys.categories(),
    queryFn: () => apiService.getCategories(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - categories change infrequently
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, category }: { id: number; category: UpdateCategoryRequest }) =>
      apiService.updateCategory(id, category),
    onSuccess: updatedCategory => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categories() });

      // Update specific category in cache
      queryClient.setQueryData<Category[]>(queryKeys.categories(), old => {
        if (!old) {
          return old;
        }
        return old.map(c => (c.id === updatedCategory.id ? updatedCategory : c));
      });
    },
    onError: error => handleMutationError(error, 'update category'),
  });
}

export function useAddCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (category: CreateCategoryRequest) => apiService.addCategory(category),
    onSuccess: newCategory => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categories() });

      // Add new category to cache
      queryClient.setQueryData<Category[]>(queryKeys.categories(), old => {
        if (!old) {
          return [newCategory];
        }
        return [...old, newCategory];
      });
    },
    onError: error => handleMutationError(error, 'create category'),
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => apiService.deleteCategory(id),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categories() });

      // Remove deleted category from cache
      queryClient.setQueryData<Category[]>(queryKeys.categories(), old => {
        if (!old) {
          return old;
        }
        return old.filter(c => c.id !== deletedId);
      });
    },
  });
}

// ============================================================================
// ACCOUNTS
// ============================================================================

export function useAccounts(enabled = true) {
  return useQuery({
    queryKey: queryKeys.accounts(),
    queryFn: () => apiService.getAccounts(),
    enabled,
    staleTime: 3 * 60 * 1000, // 3 minutes - accounts change less frequently than transactions
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ accountId, account }: { accountId: string; account: UpdateAccountRequest }) =>
      apiService.updateAccount(accountId, account),
    onSuccess: updatedAccount => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts() });

      // Update specific account in cache
      queryClient.setQueryData<Account[]>(queryKeys.accounts(), old => {
        if (!old) {
          return old;
        }
        return old.map(a => (a.accountId === updatedAccount.accountId ? updatedAccount : a));
      });
    },
  });
}

export function useAddAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (account: CreateAccountRequest) => apiService.addAccount(account),
    onSuccess: newAccount => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts() });

      // Add new account to cache
      queryClient.setQueryData<Account[]>(queryKeys.accounts(), old => {
        if (!old) {
          return [newAccount];
        }
        return [...old, newAccount];
      });
    },
    onError: error => handleMutationError(error, 'create account'),
  });
}

export function useFetchAccountDetails() {
  return useMutation({
    mutationFn: (account: Account) => apiService.fetchAccountDetails(account),
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: string) => apiService.deleteAccount(accountId),
    onSuccess: (_, deletedAccountId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts() });

      // Remove deleted account from cache
      queryClient.setQueryData<Account[]>(queryKeys.accounts(), old => {
        if (!old) {
          return old;
        }
        return old.filter(a => a.accountId !== deletedAccountId);
      });

      // Clear related data
      queryClient.removeQueries({
        queryKey: queryKeys.transactionsByAccount(deletedAccountId),
      });
      queryClient.removeQueries({
        queryKey: queryKeys.accountDetails(deletedAccountId),
      });
    },
  });
}

// ============================================================================
// TENANT
// ============================================================================

export function useTenant(enabled = true) {
  return useQuery({
    queryKey: queryKeys.tenant(),
    queryFn: () => apiService.getTenant(),
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes - tenant data rarely changes
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache very long
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenant: Tenant) => apiService.saveTenant(tenant),
    onSuccess: updatedTenant => {
      queryClient.setQueryData(queryKeys.tenant(), updatedTenant);
    },
  });
}

export function useRefreshAccessId() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiService.refreshAccessId(),
    onSuccess: updatedTenant => {
      queryClient.setQueryData(queryKeys.tenant(), updatedTenant);
    },
  });
}

export function useActivateSharedTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sharedTenantId, active }: { sharedTenantId: number; active: boolean }) =>
      apiService.activateSharedTenant(sharedTenantId, active),
    onSuccess: updatedTenant => {
      queryClient.setQueryData(queryKeys.tenant(), updatedTenant);
    },
  });
}

// ============================================================================
// PROFILE
// ============================================================================

export function useProfile(enabled = true) {
  return useQuery({
    queryKey: queryKeys.profile(),
    queryFn: () => apiService.getProfile(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - profile changes occasionally
    gcTime: 15 * 60 * 1000, // 15 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (profile: Profile) => apiService.updateProfile(profile),
    onSuccess: updatedProfile => {
      queryClient.setQueryData(queryKeys.profile(), updatedProfile);
    },
  });
}

// ============================================================================
// SHARING
// ============================================================================

export function useCreateSharingRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (command: CreateSharingRequestCommand) => apiService.createSharingRequest(command),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sharingConfig() });
    },
  });
}

export function useSharingConfiguration(enabled = true) {
  return useQuery({
    queryKey: queryKeys.sharingConfig(),
    queryFn: () => apiService.getSharingConfiguration(),
    enabled,
    staleTime: 3 * 60 * 1000, // 3 minutes - sharing config changes occasionally
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
}

export function useActionSharingResponse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ responseId, accept }: { responseId: number; accept: boolean }) =>
      apiService.actionSharingResponse(responseId, accept),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sharingConfig() });
    },
  });
}

export function useDeleteSharingRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId: number) => apiService.deleteSharingRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sharingConfig() });
    },
  });
}
